import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './PermissionScopingDemoAgentic.module.css';

type PermId = 'read' | 'send';
type Phase = 'request' | 'working' | 'ready';
type Grants = Record<PermId, boolean>;

interface LogEntry {
  time: string;
  text: string;
  state: 'done' | 'active';
}

const PERMS: { id: PermId; access: string; task: string; short: string }[] = [
  {
    id: 'read',
    access: 'See your free/busy times',
    task: 'to propose interview slots that fit your calendar',
    short: 'Calendar free/busy (read)',
  },
  {
    id: 'send',
    access: 'Send invites from your calendar',
    task: 'to book confirmed slots without you sending anything',
    short: 'Calendar invites (send)',
  },
];

interface Outcome {
  log: string;
  title: string;
  detail: string;
  you: string;
}

function outcomeFor(g: Grants): Outcome {
  if (g.read && g.send) {
    return {
      log: 'Booked interviews for 5 candidates',
      title: '5 interviews booked',
      detail: 'Invites sent from your calendar, all in free slots',
      you: 'Nothing - just show up',
    };
  }
  if (g.read) {
    return {
      log: 'Proposed slots ready for 5 candidates',
      title: '3 proposed slots per candidate',
      detail: 'All in your free time, nothing sent yet',
      you: 'Send the invites when you are happy with them',
    };
  }
  if (g.send) {
    return {
      log: 'Sent booking links for 5 candidates',
      title: 'Booking links sent',
      detail: 'Using your default hours (Mon-Fri, 10-4); clashes not checked',
      you: 'Watch for double bookings',
    };
  }
  return {
    log: 'Drafted availability requests for 5 candidates',
    title: '5 availability emails drafted',
    detail: 'Candidates reply with times that suit them',
    you: 'Send the drafts, then pick times',
  };
}

const clock = (n: number) => `11:${String(30 + n).padStart(2, '0')}`;

export function PermissionScopingDemoAgentic() {
  const [phase, setPhase] = useState<Phase>('request');
  const [grants, setGrants] = useState<Grants>({ read: false, send: false });
  const [log, setLog] = useState<LogEntry[]>([
    { time: '11:30', text: 'Shortlisted 5 candidates for Backend Engineer', state: 'done' },
    { time: '11:31', text: 'Scheduling interviews - waiting for your permission', state: 'active' },
  ]);
  const [feedback, setFeedback] = useState(
    'The agent asks only now, at the scheduling step. Allow one, both or neither - it still finishes the job.'
  );
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const list = timers.current;
    return () => list.forEach(clearTimeout);
  }, []);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const finishWith = (g: Grants, note: string) => {
    setPhase('working');
    const o = outcomeFor(g);
    setLog((l) => {
      const settled = l.map((e) => ({ ...e, state: 'done' as const }));
      return [...settled, { time: clock(settled.length + 1), text: 'Adapting plan to the access you gave', state: 'active' }];
    });
    later(() => {
      setLog((l) => {
        const settled = l.slice(0, -1).concat({ ...l[l.length - 1], state: 'done' });
        return [...settled, { time: clock(settled.length + 1), text: o.log, state: 'done' }];
      });
      setPhase('ready');
      setFeedback(note);
    }, 1100);
  };

  const toggle = (id: PermId) => setGrants((g) => ({ ...g, [id]: !g[id] }));

  const proceed = (g: Grants) => {
    setGrants(g);
    const count = Number(g.read) + Number(g.send);
    finishWith(
      g,
      count === 2
        ? 'Full access: the agent booked everything. You can still revoke either permission below.'
        : count === 1
        ? 'Partial access still got real work done. The agent adapted instead of failing.'
        : 'No access at all, and the agent is still useful. Nothing broke because a permission was denied.'
    );
  };

  const revoke = (id: PermId) => {
    const next = { ...grants, [id]: false };
    setGrants(next);
    setLog((l) => [
      ...l,
      { time: clock(l.length + 1), text: `You revoked ${PERMS.find((p) => p.id === id)?.short}`, state: 'done' },
    ]);
    finishWith(next, 'Revoked in one click. The agent switched to a plan that works without that access.');
  };

  const outcome = outcomeFor(grants);
  const active = PERMS.filter((p) => grants[p.id]);
  const grantedCount = active.length;

  return (
    <div className={kit.root}>
      <div className={kit.log} aria-label="Agent activity">
        {log.map((e, i) => (
          <div className={kit.logRow} key={i}>
            <span className={kit.logTime}>{e.time}</span>
            <span className={`${kit.logDot} ${e.state === 'done' ? kit.logDotDone : kit.logDotActive}`} aria-hidden="true" />
            <span>{e.text}</span>
            <span />
          </div>
        ))}
      </div>

      {phase === 'request' && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Permissions required · scheduling</span>
            <span>2 optional</span>
          </div>
          <h3 className={kit.proposalTitle}>To schedule interviews, I'd like to:</h3>
          <ul className={styles.perms}>
            {PERMS.map((p) => (
              <li key={p.id} className={styles.perm}>
                <div>
                  <div className={styles.access}>{p.access}</div>
                  <div className={styles.task}>{p.task}</div>
                </div>
                <button
                  type="button"
                  aria-pressed={grants[p.id]}
                  className={`${kit.btn} ${grants[p.id] ? kit.btnApprove : ''}`}
                  onClick={() => toggle(p.id)}
                >
                  {grants[p.id] ? 'Allowed' : 'Allow'}
                </button>
              </li>
            ))}
          </ul>
          <div className={`${kit.row} ${styles.actions}`}>
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => proceed(grants)}>
              {grantedCount === 0 ? 'Continue without access' : `Continue with ${grantedCount} of 2`}
            </button>
            <button
              type="button"
              className={`${kit.btn} ${kit.btnGhost}`}
              onClick={() => proceed({ read: false, send: false })}
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {phase === 'ready' && (
        <>
          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>Output · Backend Engineer interviews</span>
              <span>{grantedCount} of 2 permissions</span>
            </div>
            <h3 className={kit.proposalTitle}>{outcome.title}</h3>
            <dl className={kit.fields}>
              <dt>Done</dt>
              <dd>{outcome.detail}</dd>
              <dt>Your part</dt>
              <dd>{outcome.you}</dd>
            </dl>
          </div>

          <div className={styles.activePanel}>
            <span className={kit.label}>Active permissions</span>
            {active.length === 0 ? (
              <p className={styles.none}>None. The agent has no calendar access.</p>
            ) : (
              active.map((p) => (
                <div className={styles.activeRow} key={p.id}>
                  <span className={`${kit.chip} ${kit.chipGood}`}>{p.short}</span>
                  <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => revoke(p.id)}>
                    Revoke
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
